import { useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Row = ({ s, pick, onPick }) => (
  <li data-testid={`barcode-row-${s.slug}`} className="py-3 grid sm:grid-cols-[1fr_minmax(0,320px)_auto] gap-2 items-center">
    <div className="min-w-0">
      <p className="font-medium truncate">{s.name}</p>
      {!s.sure && <p className="text-xs text-amber-700">Comes in more than one version. Pick the one you sell.</p>}
      {s.sure && s.options.length > 1 && <p className="text-xs text-stone-500">Same product; older or newer packaging uses the other barcode.</p>}
    </div>
    <select data-testid={`barcode-select-${s.slug}`} value={pick} onChange={(e) => onPick(e.target.value)} className="rounded-xl border border-[#E7E0D3] bg-white px-3 py-2">
      {!s.sure && <option value="">Choose version…</option>}
      {s.options.map((o) => <option key={o.gtin} value={o.gtin}>{o.gtin} · {o.label}</option>)}
    </select>
    <a data-testid={`barcode-check-${s.slug}`} href={`https://www.barcodelookup.com/${pick || s.options[0].gtin}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-stone-500 hover:text-stone-800">
      Check <ExternalLink size={12} />
    </a>
  </li>
);

export const AdminBarcodeSuggestions = ({ suggestions, onSaved }) => {
  const [picks, setPicks] = useState(() => Object.fromEntries(suggestions.map((s) => [s.slug, s.sure ? s.options[0].gtin : ""])));
  const [busy, setBusy] = useState(false);
  const chosen = Object.entries(picks).filter(([, g]) => g);
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/barcodes/approve", { items: chosen.map(([slug, gtin]) => ({ slug, gtin })) });
      toast.success(`Saved ${data.saved.length} barcodes. Google picks them up on its next daily check.`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="barcode-suggestions" className="lg:col-span-2 rounded-2xl p-5 bg-white border border-[#F0EBE1] text-sm">
      <h3 className="font-heading font-bold text-lg">Barcodes I found for {suggestions.length} name-brand products</h3>
      <p className="mt-1 text-stone-600">Looked up on public product databases, and every number passes the barcode check-digit test. If you can, compare against the box, then approve. Nothing is saved until you do.</p>
      <ul className="mt-3 divide-y divide-[#F0EBE1]">
        {suggestions.map((s) => <Row key={s.slug} s={s} pick={picks[s.slug]} onPick={(g) => setPicks((p) => ({ ...p, [s.slug]: g }))} />)}
      </ul>
      <button data-testid="barcode-approve-button" disabled={busy || !chosen.length} onClick={approve} className="mt-4 px-5 py-2.5 rounded-full bg-[#1C1917] text-white disabled:opacity-50 inline-flex items-center gap-2 transition-opacity">
        <Check size={16} /> Approve {chosen.length} barcodes
      </button>
    </section>
  );
};
